import {
  CHECKOUT_SCHEMA_VERSION,
  isCheckoutRetryable,
  migrateCheckoutRecord,
  type CheckoutRecord,
} from './checkout-record.ts';
import {
  isRetryable,
  isRetryableErrorCode,
  migrateRecord,
  OUTBOX_SCHEMA_VERSION,
  type OutboxRecord,
} from './outbox-record.ts';

/**
 * G-001 — the local outbox (§9) and G-011 — the local check-out store.
 *
 * Written with the raw IndexedDB API rather than a wrapper library: the
 * participant bundle has a hard performance budget (§22) and this is two
 * object stores with simple key-value operations.
 *
 * Why two stores and not one: a check-out is keyed by the submission_id of the
 * check-in it belongs to, so the two records share an identity. A single store
 * keyed by `submissionId` could only hold one of them — saving a check-out
 * would overwrite the check-in it refers to. See `checkout-record.ts`.
 *
 * Why two *databases* and no version number anywhere
 * --------------------------------------------------
 * Check-out first shipped as a second object store inside `irace-checkin-local`,
 * which meant bumping that database's version. In a real browser this took
 * check-in down: a participant with another tab open on the previous bundle
 * holds the old version open, `onblocked` fires, the open rejects — and because
 * a check-in is written locally *before* any network call, the participant
 * could not check in at all. The screen sat on "ĐANG GỬI..." forever.
 *
 * The rule that follows: never ask for a version. `factory.open(name)` with no
 * version opens whatever the device already has and can never trigger a version
 * change, so it can never be blocked by another tab, on any device, ever. New
 * stores go in a new database instead of a new version of an old one — a
 * database nobody has open cannot be blocked either.
 *
 * `onupgradeneeded` is still needed, and still fires, for a device that has
 * never opened the database at all (version 0 -> 1). That is the only upgrade
 * this code performs, and there is nothing alive to block it.
 */

export const OUTBOX_DB_NAME = 'irace-checkin-local';
export const OUTBOX_STORE_NAME = 'outbox';
/**
 * Check-outs live in their own database, not in a version 2 of the outbox one.
 * See the note above: a brand-new name has no existing connections anywhere, so
 * creating it cannot be blocked.
 */
export const CHECKOUT_DB_NAME = 'irace-checkin-checkouts';
export const CHECKOUT_STORE_NAME = 'checkouts';

/**
 * The persistence contract the submission service and retry engine depend on.
 *
 * They take this interface rather than the concrete store so their logic can
 * be tested without a browser, and so a device where IndexedDB is unavailable
 * fails at one seam instead of everywhere.
 */
export interface OutboxStore {
  /** Save (or update) a record without changing its identity. */
  putPending(record: OutboxRecord): Promise<void>;
  /** One record by submission_id, whatever its status. */
  getPending(submissionId: string): Promise<OutboxRecord | null>;
  /** Records still worth sending, oldest first (§19 processes sequentially). */
  listPending(eventId?: string): Promise<OutboxRecord[]>;
  /**
   * Every record, whatever its status, oldest first.
   *
   * `listPending` cannot answer the check-out question: the record a returning
   * participant needs offered is precisely the one that is *accepted*, which
   * that method filters out by design.
   */
  listAll(eventId?: string): Promise<OutboxRecord[]>;
  /** Mark an attempt as started; returns the updated record. */
  markSending(submissionId: string): Promise<OutboxRecord | null>;
  /** Only ever called after HTTP 202 (AGENTS.md invariant 5). */
  markAccepted(submissionId: string): Promise<void>;
  /** Retryable codes drop back to `pending`; the rest are terminal. */
  markFailed(submissionId: string, code: OutboxRecord['lastErrorCode']): Promise<void>;
  /** §17 — accepted records are kept briefly, then cleaned up. */
  pruneAccepted(olderThanMs: number): Promise<number>;
}

export interface IndexedDbOutboxOptions {
  factory?: IDBFactory;
  now?: () => Date;
}

function promisify<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => {
      resolve(request.result);
    };
    request.onerror = () => {
      reject(request.error ?? new Error('IndexedDB request failed'));
    };
  });
}

/**
 * Open a database at whatever version the device already has it, creating
 * `storeName` only if the database did not exist at all.
 *
 * No version is passed, on purpose — see the note at the top of this file. That
 * makes three device populations converge on the same code path:
 *
 * - never opened the site: `onupgradeneeded` fires at version 0 -> 1 and
 *   creates the store; nothing is open anywhere, so nothing can block it.
 * - on the old version 1 of `irace-checkin-local`: opens version 1 as-is, no
 *   upgrade, no block, and its pending check-ins are readable untouched.
 * - already dragged to version 2 by the twenty minutes the broken bundle was
 *   live: opens version 2 as-is, no upgrade, no block. That database still
 *   carries an unused `checkouts` store. LEAVE IT THERE. Deleting an object
 *   store requires a version change, and a version change is precisely what
 *   another open tab is entitled to block — the outage this file exists to
 *   prevent. An orphan store costs nothing; tidying it costs check-in.
 *
 * `onClosed` fires when this connection gives itself up so another tab can
 * upgrade; the caller uses it to forget the handle, since a closed connection
 * throws on every later transaction.
 */
function openDatabase(
  factory: IDBFactory,
  dbName: string,
  storeName: string,
  onClosed: () => void,
): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = factory.open(dbName);
    /** `onblocked` can reject and `onsuccess` still fire later; only one wins. */
    let settled = false;

    request.onupgradeneeded = () => {
      const db = request.result;
      // Only reached on a device where this database does not exist yet. The
      // existence check still matters: it guarantees this can never drop or
      // rebuild a store holding pending check-ins a participant cannot recreate.
      if (!db.objectStoreNames.contains(storeName)) {
        db.createObjectStore(storeName, { keyPath: 'submissionId' });
      }
    };
    request.onsuccess = () => {
      const db = request.result;

      if (settled) {
        // We already rejected this open, and it succeeded afterwards anyway.
        // Nobody is waiting on this handle any more — close it, or it sits open
        // forever and blocks whatever the next open needs.
        db.close();
        return;
      }
      settled = true;

      db.onversionchange = () => {
        // Nothing this bundle does asks for a version change, so this should
        // never fire. It is kept because it costs nothing and it is the only
        // thing that would let a *future* release perform a real upgrade
        // safely: a tab that steps aside cannot block one.
        db.close();
        onClosed();
      };

      resolve(db);
    };
    request.onerror = () => {
      settled = true;
      reject(request.error ?? new Error('IndexedDB open failed'));
    };
    request.onblocked = () => {
      // Unreachable for a versionless open, which never requests a version
      // change. Kept as a backstop so that if some future change ever does ask
      // for one, the failure is visible rather than a silent hang: the caller
      // drops the cached promise, so a later attempt genuinely retries.
      settled = true;
      reject(new Error('IndexedDB open blocked by another tab'));
    };
  });
}

/**
 * Run one transaction and resolve only once it *completes*.
 *
 * The write is not durable until the transaction commits, so the caller must
 * never learn "saved" from the put request alone (§12). Shared by both stores
 * because both make that same promise to the participant.
 */
async function runTransaction<T>(
  db: IDBDatabase,
  storeName: string,
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => Promise<T> | T,
): Promise<T> {
  const tx = db.transaction(storeName, mode);
  const result = await run(tx.objectStore(storeName));

  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => {
      resolve();
    };
    tx.onerror = () => {
      reject(tx.error ?? new Error('IndexedDB transaction failed'));
    };
    tx.onabort = () => {
      reject(tx.error ?? new Error('IndexedDB transaction aborted'));
    };
  });

  return result;
}

/**
 * One connection per store instance, opened lazily. Reopening per operation
 * would add a handshake to every retry tick; holding it open costs nothing on
 * a single-page app.
 */
class LazyConnection {
  #connection: Promise<IDBDatabase> | null = null;

  constructor(
    private readonly factory: IDBFactory,
    private readonly dbName: string,
    private readonly storeName: string,
  ) {}

  get(): Promise<IDBDatabase> {
    // A failed open must not poison the store. Caching a rejection would keep
    // failing every check-in save in this tab long after the cause is gone —
    // the worst outcome this system has. Dropping the promise means the next
    // attempt genuinely re-opens.
    this.#connection ??= openDatabase(this.factory, this.dbName, this.storeName, () => {
      this.#connection = null;
    }).catch((error: unknown) => {
      this.#connection = null;
      throw error;
    });
    return this.#connection;
  }

  close(): void {
    // Swallow a rejected open: closing a connection that never opened is not
    // an error, and an unhandled rejection here would surface as a crash.
    void this.#connection?.then(
      (db) => {
        db.close();
      },
      () => {
        /* never opened */
      },
    );
    this.#connection = null;
  }
}

function requireFactory(factory: IDBFactory | undefined): IDBFactory {
  if (!factory) {
    throw new Error('IndexedDB is not available in this browser');
  }
  return factory;
}

export class IndexedDbOutbox implements OutboxStore {
  readonly #connection: LazyConnection;
  readonly #now: () => Date;

  constructor(options: IndexedDbOutboxOptions = {}) {
    this.#connection = new LazyConnection(
      requireFactory(options.factory ?? globalThis.indexedDB),
      OUTBOX_DB_NAME,
      OUTBOX_STORE_NAME,
    );
    this.#now = options.now ?? (() => new Date());
  }

  async #transaction<T>(
    mode: IDBTransactionMode,
    run: (store: IDBObjectStore) => Promise<T> | T,
  ): Promise<T> {
    return runTransaction(await this.#connection.get(), OUTBOX_STORE_NAME, mode, run);
  }

  /** Read-modify-write inside one transaction so concurrent ticks cannot interleave. */
  async #update(
    submissionId: string,
    mutate: (record: OutboxRecord) => OutboxRecord,
  ): Promise<OutboxRecord | null> {
    return this.#transaction('readwrite', async (store) => {
      const existing = migrateRecord(await promisify(store.get(submissionId)));
      if (!existing) return null;
      const updated = mutate(existing);
      await promisify(store.put(updated));
      return updated;
    });
  }

  async putPending(record: OutboxRecord): Promise<void> {
    // keyPath is submissionId, so a retry of the same submission overwrites
    // rather than duplicating (AGENTS.md invariant 4).
    await this.#transaction('readwrite', (store) =>
      promisify(store.put({ ...record, schemaVersion: OUTBOX_SCHEMA_VERSION })),
    );
  }

  async getPending(submissionId: string): Promise<OutboxRecord | null> {
    return this.#transaction('readonly', async (store) =>
      migrateRecord(await promisify(store.get(submissionId))),
    );
  }

  async listPending(eventId?: string): Promise<OutboxRecord[]> {
    const all = await this.#transaction('readonly', (store) =>
      promisify(store.getAll() as IDBRequest<unknown[]>),
    );

    return all
      .map((stored) => migrateRecord(stored))
      .filter((record): record is OutboxRecord => record !== null)
      .filter((record) => isRetryable(record))
      .filter((record) => eventId === undefined || record.eventId === eventId)
      .sort((a, b) => a.clientCreatedAt.localeCompare(b.clientCreatedAt));
  }

  async listAll(eventId?: string): Promise<OutboxRecord[]> {
    const all = await this.#transaction('readonly', (store) =>
      promisify(store.getAll() as IDBRequest<unknown[]>),
    );

    return all
      .map((stored) => migrateRecord(stored))
      .filter((record): record is OutboxRecord => record !== null)
      .filter((record) => eventId === undefined || record.eventId === eventId)
      .sort((a, b) => a.clientCreatedAt.localeCompare(b.clientCreatedAt));
  }

  async markSending(submissionId: string): Promise<OutboxRecord | null> {
    const at = this.#now().toISOString();
    return this.#update(submissionId, (record) => ({
      ...record,
      status: 'sending',
      attemptCount: record.attemptCount + 1,
      lastAttemptAt: at,
    }));
  }

  async markAccepted(submissionId: string): Promise<void> {
    await this.#update(submissionId, (record) => {
      const { lastErrorCode: _cleared, ...rest } = record;
      return { ...rest, status: 'accepted', lastAttemptAt: this.#now().toISOString() };
    });
  }

  async markFailed(submissionId: string, code: OutboxRecord['lastErrorCode']): Promise<void> {
    await this.#update(submissionId, (record) => ({
      ...record,
      status: isRetryableErrorCode(code) ? 'pending' : 'failed',
      lastErrorCode: code,
      lastAttemptAt: this.#now().toISOString(),
    }));
  }

  async pruneAccepted(olderThanMs: number): Promise<number> {
    const cutoff = this.#now().getTime() - olderThanMs;

    return this.#transaction('readwrite', async (store) => {
      const all = await promisify(store.getAll() as IDBRequest<unknown[]>);
      let removed = 0;

      for (const stored of all) {
        const record = migrateRecord(stored);
        if (!record || record.status !== 'accepted') continue;
        const at = Date.parse(record.lastAttemptAt ?? record.clientCreatedAt);
        if (Number.isNaN(at) || at > cutoff) continue;
        await promisify(store.delete(record.submissionId));
        removed += 1;
      }

      return removed;
    });
  }

  close(): void {
    this.#connection.close();
  }
}

/**
 * The persistence contract the check-out service depends on.
 *
 * Deliberately narrower than `OutboxStore`: there is no `pruneAccepted`.
 * Accepted check-ins are swept after a few hours because the organizer already
 * has them, but a check-out saved on a device with no signal is *pending*, and
 * a sweep that could not tell the two apart would delete exactly the
 * departures that are hardest to collect.
 */
export interface CheckoutStore {
  /** Save (or update) a check-out without changing its identity. */
  put(record: CheckoutRecord): Promise<void>;
  /** One check-out by the check-in's submission_id, whatever its status. */
  get(submissionId: string): Promise<CheckoutRecord | null>;
  /** Check-outs still worth sending, oldest first. */
  listPending(eventId?: string): Promise<CheckoutRecord[]>;
  /**
   * Every check-out, whatever its status, oldest first.
   *
   * The resume rule has to see accepted and failed check-outs too: all three
   * statuses mean the participant already tapped the button, and only
   * `listPending` would hide two of them.
   */
  listAll(eventId?: string): Promise<CheckoutRecord[]>;
  /** Mark an attempt as started; returns the updated record. */
  markSending(submissionId: string): Promise<CheckoutRecord | null>;
  /** Only ever called after HTTP 202 (AGENTS.md invariant 5). */
  markAccepted(submissionId: string): Promise<void>;
  /** Retryable codes drop back to `pending`; the rest are terminal. */
  markFailed(submissionId: string, code: CheckoutRecord['lastErrorCode']): Promise<void>;
}

export type IndexedDbCheckoutOptions = IndexedDbOutboxOptions;

export class IndexedDbCheckoutStore implements CheckoutStore {
  readonly #connection: LazyConnection;
  readonly #now: () => Date;

  constructor(options: IndexedDbCheckoutOptions = {}) {
    this.#connection = new LazyConnection(
      requireFactory(options.factory ?? globalThis.indexedDB),
      CHECKOUT_DB_NAME,
      CHECKOUT_STORE_NAME,
    );
    this.#now = options.now ?? (() => new Date());
  }

  async #transaction<T>(
    mode: IDBTransactionMode,
    run: (store: IDBObjectStore) => Promise<T> | T,
  ): Promise<T> {
    return runTransaction(await this.#connection.get(), CHECKOUT_STORE_NAME, mode, run);
  }

  /** Read-modify-write inside one transaction so concurrent ticks cannot interleave. */
  async #update(
    submissionId: string,
    mutate: (record: CheckoutRecord) => CheckoutRecord,
  ): Promise<CheckoutRecord | null> {
    return this.#transaction('readwrite', async (store) => {
      const existing = migrateCheckoutRecord(await promisify(store.get(submissionId)));
      if (!existing) return null;
      const updated = mutate(existing);
      await promisify(store.put(updated));
      return updated;
    });
  }

  async put(record: CheckoutRecord): Promise<void> {
    // keyPath is submissionId, so tapping "check out" twice updates one record
    // rather than creating a second (AGENTS.md invariant 4).
    await this.#transaction('readwrite', (store) =>
      promisify(store.put({ ...record, schemaVersion: CHECKOUT_SCHEMA_VERSION })),
    );
  }

  async get(submissionId: string): Promise<CheckoutRecord | null> {
    return this.#transaction('readonly', async (store) =>
      migrateCheckoutRecord(await promisify(store.get(submissionId))),
    );
  }

  async listPending(eventId?: string): Promise<CheckoutRecord[]> {
    const all = await this.#transaction('readonly', (store) =>
      promisify(store.getAll() as IDBRequest<unknown[]>),
    );

    return all
      .map((stored) => migrateCheckoutRecord(stored))
      .filter((record): record is CheckoutRecord => record !== null)
      .filter((record) => isCheckoutRetryable(record))
      .filter((record) => eventId === undefined || record.eventId === eventId)
      .sort((a, b) => a.clientCheckedOutAt.localeCompare(b.clientCheckedOutAt));
  }

  async listAll(eventId?: string): Promise<CheckoutRecord[]> {
    const all = await this.#transaction('readonly', (store) =>
      promisify(store.getAll() as IDBRequest<unknown[]>),
    );

    return all
      .map((stored) => migrateCheckoutRecord(stored))
      .filter((record): record is CheckoutRecord => record !== null)
      .filter((record) => eventId === undefined || record.eventId === eventId)
      .sort((a, b) => a.clientCheckedOutAt.localeCompare(b.clientCheckedOutAt));
  }

  async markSending(submissionId: string): Promise<CheckoutRecord | null> {
    const at = this.#now().toISOString();
    return this.#update(submissionId, (record) => ({
      ...record,
      status: 'sending',
      attemptCount: record.attemptCount + 1,
      lastAttemptAt: at,
    }));
  }

  async markAccepted(submissionId: string): Promise<void> {
    await this.#update(submissionId, (record) => {
      const { lastErrorCode: _cleared, ...rest } = record;
      return { ...rest, status: 'accepted', lastAttemptAt: this.#now().toISOString() };
    });
  }

  async markFailed(submissionId: string, code: CheckoutRecord['lastErrorCode']): Promise<void> {
    await this.#update(submissionId, (record) => ({
      ...record,
      status: isRetryableErrorCode(code) ? 'pending' : 'failed',
      lastErrorCode: code,
      lastAttemptAt: this.#now().toISOString(),
    }));
  }

  close(): void {
    this.#connection.close();
  }
}
